import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Toast from '../../components/Common/Toast';
import { getSunatMappingsAll, updateSunatMapping } from '../../services/sunatMappingService';
import { supplierService } from '../../services/supplierService';
import { productService } from '../../services/productService';
import { getUnits } from '../../services/unitsService';

const SunatMappings = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [search, setSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editProductId, setEditProductId] = useState('');
  const [editUnitId, setEditUnitId] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const showToast = useCallback((message, type = 'info') => {
    setToast({ isOpen: true, message, type });
  }, []);

  const hasLoadedRef = useRef(false);

  const loadFormData = useCallback(async () => {
    try {
      const [suppliersRes, productsRes, unitsRes] = await Promise.all([
        supplierService.getSuppliers({ per_page: 200 }),
        productService.getProducts({ per_page: 500 }),
        getUnits({ per_page: 100 }),
      ]);
      if (suppliersRes.success) setSuppliers(suppliersRes.data || []);
      if (productsRes.success) setProducts(Array.isArray(productsRes.data) ? productsRes.data : productsRes.data?.data || []);
      if (unitsRes.success) setUnits(unitsRes.data || []);
    } catch {
      showToast('Error al cargar datos del formulario', 'error');
    }
  }, [showToast]);

  const loadMappings = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: 20 };
      if (supplierFilter) params.supplier_id = supplierFilter;
      if (search) params.search = search;
      const res = await getSunatMappingsAll(params);
      if (res.success) {
        setMappings(res.data.data || []);
        setPage(res.data.current_page || 1);
        setLastPage(res.data.last_page || 1);
        setTotal(res.data.total || 0);
      }
    } catch {
      showToast('Error al cargar mapeos', 'error');
    } finally {
      setLoading(false);
    }
  }, [supplierFilter, search, showToast]);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadFormData();
      loadMappings();
    }
  }, [loadFormData, loadMappings]);

  const handleSearch = useCallback(() => {
    setPage(1);
    loadMappings(1);
  }, [loadMappings]);

  const handleSupplierChange = useCallback((e) => {
    setSupplierFilter(e.target.value);
    setPage(1);
  }, []);

  useEffect(() => {
    loadMappings(1);
  }, [supplierFilter]);

  const suppliersMap = useMemo(
    () => new Map(suppliers.map((s) => [s.id, s])),
    [suppliers]
  );

  const productsMap = useMemo(
    () => new Map(products.map((p) => [p.id, p])),
    [products]
  );

  const unitsMap = useMemo(
    () => new Map(units.map((u) => [u.id, u])),
    [units]
  );

  const startEditing = useCallback((m) => {
    setEditingId(m.id);
    setEditProductId(m.product_id ?? '');
    setEditUnitId(m.unit_id ?? '');
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingId(null);
    setEditProductId('');
    setEditUnitId('');
  }, []);

  const saveEdit = useCallback(async (id) => {
    setSavingId(id);
    try {
      const res = await updateSunatMapping(id, {
        product_id: editProductId,
        unit_id: editUnitId || null,
      });
      if (res.success) {
        setMappings((prev) =>
          prev.map((m) => (m.id === id ? { ...m, ...res.data, product_id: parseInt(editProductId), unit_id: editUnitId ? parseInt(editUnitId) : null } : m))
        );
        setEditingId(null);
        showToast('Mapeo actualizado exitosamente', 'success');
      } else {
        showToast(res.message || 'Error al actualizar mapeo', 'error');
      }
    } catch {
      showToast('Error de conexión al actualizar mapeo', 'error');
    } finally {
      setSavingId(null);
    }
  }, [editProductId, editUnitId, showToast]);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Relación Productos SUNAT</h1>
          <p className="text-gray-600 mt-1">
            Gestiona el mapeo entre productos del XML SUNAT y productos internos de Contify
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex flex-wrap gap-3 items-end">
          <div className="w-56">
            <label className="block text-xs font-medium text-gray-500 mb-1">Proveedor</label>
            <select
              value={supplierFilter}
              onChange={handleSupplierChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">Todos los proveedores</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.document_number})
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Código o nombre del producto SUNAT..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
              <button
                type="button"
                onClick={handleSearch}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors"
              >
                Buscar
              </button>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {total} registro(s)
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Proveedor</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Código SUNAT</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Producto SUNAT</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Unidad SUNAT</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Producto Interno</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Unidad Interna</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    Cargando...
                  </td>
                </tr>
              ) : mappings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    No se encontraron mapeos
                  </td>
                </tr>
              ) : (
                mappings.map((m) => {
                  const isEditing = editingId === m.id;
                  const isSaving = savingId === m.id;
                  const supplier = suppliersMap.get(m.supplier_id);
                  const product = isEditing
                    ? productsMap.get(parseInt(editProductId))
                    : m.product;
                  const unit = isEditing
                    ? unitsMap.get(parseInt(editUnitId))
                    : m.unit;
                  return (
                    <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-gray-900 max-w-[200px] truncate">
                        {supplier?.name || `ID ${m.supplier_id}`}
                      </td>
                      <td className="py-3 px-4 text-gray-900 font-mono text-xs">
                        {m.supplier_product_code}
                      </td>
                      <td className="py-3 px-4 text-gray-900 max-w-[250px] truncate">
                        {m.supplier_product_name || '—'}
                      </td>
                      <td className="py-3 px-4 text-gray-500">
                        {m.supplier_unit_code || '—'}
                      </td>
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <select
                            value={editProductId}
                            onChange={(e) => setEditProductId(e.target.value)}
                            className="w-48 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          >
                            <option value="">— Sin producto —</option>
                            {products.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.sku} — {p.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-gray-900">
                            {product ? `${product.sku} — ${product.name}` : '—'}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <select
                            value={editUnitId}
                            onChange={(e) => setEditUnitId(e.target.value)}
                            className="w-32 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          >
                            <option value="">— Sin unidad —</option>
                            {units.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.code} — {u.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-gray-900">
                            {unit ? `${unit.code} — ${unit.name}` : '—'}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {isEditing ? (
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={cancelEditing}
                              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              onClick={() => saveEdit(m.id)}
                              disabled={isSaving || !editProductId}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSaving ? 'Guardando...' : 'Guardar'}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEditing(m)}
                            className="px-3 py-1.5 text-xs font-medium text-teal-600 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
                          >
                            Editar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {lastPage > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <button
              type="button"
              onClick={() => loadMappings(page - 1)}
              disabled={page <= 1}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span className="text-xs text-gray-500">
              Página {page} de {lastPage}
            </span>
            <button
              type="button"
              onClick={() => loadMappings(page + 1)}
              disabled={page >= lastPage}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default SunatMappings;
